import NavBar from './Navbar';
import { KView, PageContext } from '@/component/RtlLayout';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Text } from 'react-native';
import { styles, vh } from '@/common/style';
import { useCardList } from './model';
import { ItemList } from './ItemList/ItemList';
import SpecLinePopWrap from '@/page/detail/views/content/components/SpecLinePopWrap';
import { useExtParamsFromUrl } from '@/page/detail/views/content/utils';
import { useAddToCartAnimation } from '@/hooks/useAddToCartAnimation';
import { useGetStoreCartItem, useQuery } from './hooks/useGetStoreCartItem';
import { BottomCheck } from './BottomCheck';
import { useShopInfoStore } from './store/shopInfoStore';
import SrcStyles from './addOnHelper.styl';
import { convertStyles, isAndroid, isIOS, useResume } from '@kibt/krn-utils';
import { useConstructor } from '@/hooks/useLifecycle';
import { isWeb, screenHeight } from '@/common/device-info';
import { $k18n } from '@/common/i18n';
import { k18nPro } from '@/component/k18n/K18n';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import isEqual from 'lodash.isequal';
import { sendClickAddonItem } from '@/page/recommendGoods/logger';
import { useQuerys } from '@/hooks/useQuery';

const Styles = convertStyles(SrcStyles);

interface AddOnHelperProps {
    popupProps?: {
        // 是否是一个弹窗
        isPopup: boolean;
        // 关闭弹窗
        onClose: () => void;
        // 来源
        source: 'recommendGoods';
        addonQuery: {
            photoId: string | number;
            authorId: string | number;
            couponBatchId: string | number;
            itemId: string | number;
            merchantId: string | number;
        };
    };
    pageStatus?: string; // 半屏/全屏
    pageHeight?: number;
    isFromDetail?: boolean; // 是否来自详情页
    targetMakeupCoupon?: any; // 目标凑单券
}

const textStyle = {
    border: styles({
        fontWeight: '500',
        color: '#ff4906',
        fontSize: 14,
    }),
    highLight: styles({
        color: '#ff4906',
        fontWeight: '700',
        fontSize: 16,
    }),
};

const MemoNavBar = React.memo(NavBar, (preProps, nextProps) => {
    return isEqual(preProps.title, nextProps.title);
});

export const AddOnHelper: FC<AddOnHelperProps> = (props) => {
    // 在双列推荐里作为弹窗使用
    const { popupProps, pageStatus, pageHeight, isFromDetail, targetMakeupCoupon } = props;
    const model = useCardList({ source: popupProps?.source || 'shoppingCar', addonQuery: popupProps?.addonQuery });
    const { query } = useQuery();
    const [init, setInit] = useState(false);

    // 请求接口数据
    useConstructor(() => {
        model
            .loadData({
                liveId: query.liveId,
                authorId: query.authorId,
                couponBatchId: query.couponBatchId,
                itemId: query.itemId,
                belongMerchantId: query.merchantId,
            })
            .then(() => {
                setInit(true);
            });
    });

    const [curItemId, setCurItemId] = useState(0);
    const [height, setHeight] = useState(null as number);
    const [curItem, setCurItem] = useState(null);
    const [showPop, setShowPop] = useState(false);
    const setParams = useMemo(() => ({ setCurItemId, setCurItem, setShowPop }), [setCurItemId, setCurItem, setShowPop]);
    const specLineRef = useRef(null);
    const { loadShopData: loadSelectHaltScreen } = useGetStoreCartItem();
    const storeCartItem = useShopInfoStore((state) => state.storeCartItem);
    const setStoreCartItem = useShopInfoStore((state) => state.setStoreCartItem);
    const cartIconRef = useRef(null);
    const { AddToCartAnimation, CartIconAnimation, startAnimation } = useAddToCartAnimation({ cartIconRef, initialImagePosition: { y: vh(20) } });
    const selectedCartItemList = useMemo(() => {
        return storeCartItem?.cartShopList[0]?.cartItemList.filter((item) => item.isSelected);
    }, [storeCartItem, storeCartItem?.cartShopList[0]?.cartItemList]);
    const inset = useSafeAreaInsets();
    const insetBottom = isIOS ? inset.bottom : 0;

    const refreshCart = () => {
        loadSelectHaltScreen();
    };
    const getHighlightText = (displayAmount) => {
        return <Text style={textStyle.highLight}>{displayAmount}</Text>;
    };

    const discountText = useMemo(() => {
        // 如果未选中商品
        const isNoSelected = selectedCartItemList?.length === 0;
        const targetCoupon =
            targetMakeupCoupon.discountFeeType === 3
                ? storeCartItem?.cartShopList[0]?.shopEvaluate?.shopEvaluateCoupon?.shippingMakeupCoupons
                : storeCartItem?.cartShopList[0]?.shopEvaluate?.shopEvaluateCoupon?.itemMakeupCoupons;

        let { currentLevel: currentLevelCoupon, nextLevel: nextLevelCoupon, amountToNextLevel, moreDeductOfNextLevel } = targetCoupon || {};

        // 没选中时，使用shopPromotion中的数据
        if (isNoSelected) {
            const shopPromotionCoupon =
                targetMakeupCoupon.discountFeeType === 3
                    ? storeCartItem?.cartShopList[0]?.shopPromotion?.shippingAvailableCoupon
                    : storeCartItem?.cartShopList[0]?.shopPromotion?.itemAvailableCoupon;

            currentLevelCoupon = null;
            nextLevelCoupon = shopPromotionCoupon;
            amountToNextLevel = shopPromotionCoupon?.threshold;
            moreDeductOfNextLevel = shopPromotionCoupon?.maxDeductAmount;
        }

        // 要凑的券是无门槛的
        const isNextCouponNoThreshold = nextLevelCoupon && nextLevelCoupon.threshold.amount === 0;

        const getBenifitsText = (discount) => (targetMakeupCoupon.discountFeeType === 3 ? getFreeShippingText() : getProductDiscountText(discount));
        const getProductDiscountText = (discount) =>
            $k18n({
                text: '{discount} OFF',
                params: { discount },
            });
        const getFreeShippingText = () => $k18n({ id: 'free_shipping_upper', text: '包邮' });

        const outerStyle = {
            fontWeight: '500',
            color: '#000',
        };

        // 当前购物车为空或者未选中商品，此时 current 一定都为空
        if (isNoSelected) {
            if (isNextCouponNoThreshold) {
                // 加购即享xxx优惠券
                return k18nPro({
                    id: 'k_379987',
                    text: '加购即享{benefits}',
                    outerStyle,
                    params: { benefits: getHighlightText(getBenifitsText(nextLevelCoupon.maxDeductAmount?.displayAmount)) },
                });
            }
        }

        // 已凑一张低门槛券
        if (currentLevelCoupon) {
            return k18nPro({
                id: 'k_407641',
                text: '获得{num}，去结算吧',
                outerStyle,
                params: {
                    num: getHighlightText(getBenifitsText(currentLevelCoupon?.deductAmount?.displayAmount)),
                },
            });
        }

        // 凑商品券
        if (nextLevelCoupon) {
            return k18nPro({
                id: 'k_407642',
                text: '再加购{num1}可享受{num2}优惠',
                outerStyle,
                params: {
                    num1: getHighlightText(amountToNextLevel?.displayAmount),
                    num2: getHighlightText(getBenifitsText(nextLevelCoupon?.maxDeductAmount?.displayAmount)),
                },
            });
        }
    }, [storeCartItem, selectedCartItemList?.length, targetMakeupCoupon]);

    const { foldRatio } = useQuerys();
    const getItemListHeight = (height) => {
        // IOS 半屏容器的实际高度 还是全屏的高度
        // 所以在半屏时，需要乘半屏比例，获取实际高度
        if (height) {
            // 短视频是可拖动的半屏容器，有foldRatio吗？
            const foldRatioNum = isIOS && pageStatus === 'halfScreen' ? (foldRatio ? Number(foldRatio) : 1) : 1;
            return height * foldRatioNum - 72 - 48 - insetBottom;
        }

        return '100%';
    };

    useResume(() => {
        model.loadData({
            liveId: query.liveId,
            authorId: query.authorId,
            couponBatchId: query.couponBatchId,
            itemId: query.itemId,
            belongMerchantId: query.merchantId,
        });
        refreshCart();
    });

    useEffect(() => {
        if (height && isIOS) {
            if (props.pageStatus === 'fullScreen') {
                setHeight(screenHeight * 0.81);
            } else {
                setHeight(screenHeight);
            }
        }
    }, [height, props.pageStatus]);

    useEffect(() => {
        loadSelectHaltScreen();
    }, []);

    const { getExtParamsFromUrl } = useExtParamsFromUrl();

    return (
        <PageContext.Provider value={{ Styles: Styles }}>
            <KView
                key={height}
                rtlClassName={['add-on-helper', isWeb ? '' : 'add-on-helper-rn']}
                collapsable={false}
                style={styles({ height: height || '100%', display: 'flex', backgroundColor: 'white' })}
                onLayout={(event) => {
                    if (height) return;
                    // console.log('props.pageStatus', props.pageStatus);
                    if (props.pageStatus === 'halfScreen') {
                        const { height: _height } = event.nativeEvent.layout;
                        const min = Math.min(_height, vh(100));
                        // console.log('vh(100)', vh(100));
                        // console.log('min', min);
                        setHeight(isWeb ? vh(100) : min);
                    } else {
                        setHeight(vh(81));
                    }
                }}
            >
                <MemoNavBar onBack={popupProps?.onClose} title={discountText} />
                <KView>
                    <ItemList init={init} model={model} setParams={setParams} height={getItemListHeight(height)} />
                </KView>
                <SpecLinePopWrap
                    ref={specLineRef}
                    type="cart"
                    showPop={showPop}
                    itemId={curItemId}
                    item={curItem}
                    closePop={() => {
                        setShowPop(false);
                    }}
                    addToCartApiLog={(params) => {
                        sendClickAddonItem({
                            itemId: params?.itemId,
                            skuId: params?.skuId,
                            batchId: targetMakeupCoupon?.batchId,
                            clickArea: 'cart',
                        });
                    }}
                    showAfterCouponPrice={false}
                    addToCartLog={() => {}}
                    addToCartCallBack={refreshCart}
                    extParamsCustom={curItem ? getExtParamsFromUrl(curItem.jumpUrl) : null}
                    addToCartAnimation={startAnimation}
                    ifForbidWebDrag={false}
                    popupStyle={props.pageStatus === 'halfScreen' && isAndroid ? { height: '100%' } : {}}
                />
                <BottomCheck
                    pageStatus={pageStatus}
                    pageHeight={pageHeight}
                    height={height}
                    storeCartItem={storeCartItem}
                    targetMakeupCoupon={targetMakeupCoupon}
                    CartIconAnimation={CartIconAnimation}
                    cartIconRef={cartIconRef}
                />
                {/* init 之后再挂载，否则会和弹窗动画冲突 */}
                {init ? (
                    <AddToCartAnimation
                        image={{
                            source: { uri: specLineRef.current?.specHeaderImageSource || '' },
                            style: { width: 128, height: 128 },
                        }}
                    />
                ) : null}
            </KView>
        </PageContext.Provider>
    );
};
